import { TREND_GROUPS } from '../../../../constants';
import { FOCUSED_COLOR, getTrendColor, MY_COMPANY_COLOR } from '../../../../utils/chart';
import createInfoboxMarkup from '../../../infobox/createInfoboxMarkup';

const { TREND_POSITIVE, TREND_NEUTRAL, TREND_NEGATIVE } = TREND_GROUPS;

export const CLUSTER_OPTIONS = {
  GROUP: 'GROUP',
  TREND: 'TREND',
};

export const mapPropsToDto = ({ data, dataKey, isReversedTrend, theme }) => {
  return data.map((domainEntree) => {
    const trend = domainEntree?.[dataKey].trend;
    const score = domainEntree?.[dataKey].score;

    const trendGroup = getTrendGroup(trend, score, isReversedTrend);
    const trendColor = getTrendColor({
      trend,
      score,
      isReversed: isReversedTrend,
      theme,
    });
    return {
      ...domainEntree,
      group: domainEntree.groups?.[0] || '',
      score,
      trend,
      active: true,
      color: trendColor,
      trendGroup,
    };
  });
};

export const clusterData = ({ dtoList, clusterBy }) => {
  const bubblesData = {
    name: 'root',
    children: dtoList,
  };

  if (clusterBy.includes(CLUSTER_OPTIONS.TREND)) {
    bubblesData.children = [
      clusterByField({
        dtoList,
        field: 'trendGroup',
        value: TREND_GROUPS.TREND_POSITIVE,
        isShowBorder: false,
      }),
      clusterByField({
        dtoList,
        field: 'trendGroup',
        value: TREND_GROUPS.TREND_NEUTRAL,
        isShowBorder: false,
      }),
      clusterByField({
        dtoList,
        field: 'trendGroup',
        value: TREND_GROUPS.TREND_NEGATIVE,
        isShowBorder: false,
      }),
    ];

    if (clusterBy.includes(CLUSTER_OPTIONS.GROUP)) {
      bubblesData.children = bubblesData.children.map((dto) => ({
        children: clusterByGroups(dto.children),
      }));

      return bubblesData;
    }
  }

  if (clusterBy.includes(CLUSTER_OPTIONS.GROUP)) {
    bubblesData.children = clusterByGroups(bubblesData.children);
  }

  return bubblesData;
};

export const clusterByField = ({ dtoList, addClusterNode, field, value, isShowBorder }) => {
  const children = dtoList.filter((dto) => dto[field] === value).map((dto) => ({ type: 'leaf', ...dto }));

  if (addClusterNode && children.length) {
    const maxScore = Math.max.apply(
      Math,
      children.map(({ score }) => score),
    );

    children.push({
      domain: value,
      score: maxScore * 0.5,
      isClusterLabel: true,
    });
  }

  return {
    type: 'node',
    clusterField: field,
    clusterValue: value,
    name: `cluster-${value}`,
    isShowBorder,
    score: children.reduce((x, { score }) => {
      return x + score;
    }, 0),
    children,
  };
};

const clusterByGroups = (dtoList) => {
  const groups = [...new Set(dtoList.map(({ group }) => group))];

  return groups.map((group) =>
    clusterByField({
      dtoList,
      field: 'group',
      value: group,
      addClusterNode: true,
      isShowBorder: true,
    }),
  );
};

export const bubbleFontSize = ({ isClusterLabel, r }) => {
  const factor = isClusterLabel ? 1 / 4 : 1 / 5;
  return r * factor;
};

export const bubbleTextColor = ({ isClusterLabel, labelsColor, inFocus, myCompany }) => {
  if (isClusterLabel) {
    return labelsColor;
  }

  if (myCompany) {
    return MY_COMPANY_COLOR;
  }

  if (inFocus) {
    return FOCUSED_COLOR;
  }

  return 'white';
};

export const truncateTextLabel = ({ r, title, fontSize }) => {
  const diameter = 2 * r;
  const maxWidth = 0.8 * diameter;

  const estimatedWidth = title.length * 0.5 * fontSize;

  if (estimatedWidth <= maxWidth) {
    return title;
  }
  const textLimit = Math.floor((title.length * maxWidth) / estimatedWidth) - 3;
  return title.substring(0, textLimit) + '...';
};

export const createTooltipContent = (event, dtoList) => {
  const domainId = event?.target?.dataset.id;

  if (!domainId) {
    return '';
  }

  const { domain, score, trend, color, clusterLabel, companyName, label } = dtoList.find(
    ({ domain }) => domainId === domain,
  );

  return createInfoboxMarkup({
    title: label || domain,
    ...(companyName ? { subTitle: domain } : {}),
    marker: !clusterLabel && { color },
    records: clusterLabel
      ? []
      : [
          { name: 'Score', value: score },
          { name: 'Trend', value: trend },
        ],
  });
};

const MIN_SIGNIFICANT_DIFFERENCE = 0.1;

export const getTrendGroup = (trend, score, isReversed) => {
  if (trend - score > MIN_SIGNIFICANT_DIFFERENCE) {
    return isReversed ? TREND_NEGATIVE : TREND_POSITIVE;
  }
  if (score - trend > MIN_SIGNIFICANT_DIFFERENCE) {
    return isReversed ? TREND_POSITIVE : TREND_NEGATIVE;
  }
  return TREND_NEUTRAL;
};
